<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from '@/composables/use-i18n';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { HistoryService } from '@/services/history/service/history.service';
import { useRoute, useRouter } from 'vue-router/composables';
import { useDisposes } from '@analytics/shared/composables/use-disposes';
import { Tooltip } from 'element-ui';
import { HistoryItem } from '../../../services/history/type/history.type';
import '@patsnap-ui/icon/assets/solid/Menu3.svg';
import '@patsnap-ui/icon/assets/solid/info.svg';
import SvgIcon from '@analytics/shared/components/SvgIcon.vue';
import IconCopilot from '@/presentation/components/icons/IconCopilot.vue';
import IconHistoryMenuClose from '@/presentation/components/icons/IconHistoryMenuClose.vue';
import IconHistoryMenuOpen from '@/presentation/components/icons/IconHistoryMenuOpen.vue';
import _ from 'lodash';
import ClickOutside from 'vue-click-outside';
import {
    HISTORY_DEFAULT_PAGE,
    HISTORY_LIST_LIMIT,
    OPEN_DELAY,
    HISTORY_TYPE_MAP,
    PATH_MAP,
} from '@/constants';

const TRIGGER_NEXT_PAGE_DISTANCE = 50;
const TRIGGER_NEXT_PAGE_THROTTLED_TIME = 800;

const vClickOutside = ClickOutside;
const { disposes } = useDisposes();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const historyListRef = ref();

const taskId = computed(() => route.query.taskId as string);

const historyService = injectService<HistoryService>(HistoryService);
const historyState = ref(historyService.getModelData());

disposes.push(
    historyService.onStatesChange(states => (historyState.value = states)),
);

historyService.getHistoryList({
    page: HISTORY_DEFAULT_PAGE,
    limit: HISTORY_LIST_LIMIT,
});

function handleHistoryMenu(showHistoryList: boolean) {
    historyService.toggleHistoryList(showHistoryList);
}

function handleHistoryItem(item: HistoryItem) {
    if (!item.isRead) {
        historyService.putHistoryRead(item.taskId);
    }
    if (taskId.value === item.taskId) return;

    const pathKey = PATH_MAP[item.type];
    const path = `/${ pathKey }`;
    router.push({ path, query: { taskId: item.taskId } });
}

function handleScroll() {
    if (!historyListRef.value) return;

    const historyList = historyListRef.value as HTMLElement;
    const { scrollTop, scrollHeight, clientHeight } = historyList;

    if (scrollTop + clientHeight >= scrollHeight - TRIGGER_NEXT_PAGE_DISTANCE) {
        historyService.getNextPageHistory();
    }
}

const handleScrollThrottled = _.throttle(
    handleScroll,
    TRIGGER_NEXT_PAGE_THROTTLED_TIME,
);

function handleGoHome() {
    router.push({ path: '/' });
}

function handleClickOutside() {
    historyService.toggleHistoryList(false);
}
</script>

<template>
    <div
        v-click-outside="handleClickOutside"
        class="history-menu pa-fixed pa-left-0 pa-top-16 pa-z-50"
        :class="{ 'pa-h-full': historyState.showHistoryList }"
    >
        <div
            v-if="historyState.showHistoryList"
            class="history-menu-list-wrap pa-h-full pa-w-[240px] pa-overflow-hidden pa-bg-white pa-p-2"
        >
            <div
                class="pa-flex pa-h-14 pa-items-center pa-justify-between pa-border-b pa-border-grey-40 pa-pb-4"
            >
                <div
                    class="pa-flex pa-w-[186px] pa-cursor-pointer pa-items-center pa-rounded-lg hover:pa-bg-grey-20"
                    @click="handleGoHome"
                >
                    <IconCopilot class="pa-mx-2 pa-my-1 pa-h-8 pa-w-8" />

                    <span class="pa-text-base pa-font-semibold pa-text-black">
                        {{ t('history.header') }}
                    </span>
                </div>

                <div
                    class="pa-cursor-pointer pa-rounded hover:pa-bg-grey-20"
                    @click.stop="handleHistoryMenu(false)"
                >
                    <IconHistoryMenuClose class="pa-m-1 pa-h-6 pa-w-6" />
                </div>
            </div>

            <div class="pa-mb-2 pa-mt-4 pa-px-2">
                <span
                    class="pa-flex pa-items-center pa-text-sm pa-font-semibold pa-text-grey-90"
                >
                    <span>
                        {{ t('history.title') }}
                    </span>
                    <Tooltip
                        :content="t('history.tip')"
                        effect="light"
                        :visibleArrow="false"
                        placement="top"
                        popperClass="history-menu-tooltip"
                        :openDelay="OPEN_DELAY"
                    >
                        <SvgIcon
                            class="pa-m-1 pa-h-5 pa-w-5 pa-fill-grey-55 hover:pa-fill-grey-450"
                            icon="SolidInfo"
                        />
                    </Tooltip>
                </span>
            </div>

            <div
                ref="historyListRef"
                class="history-menu-list pa-h-[calc(100%_-_160px)] pa-overflow-auto"
                @scroll="handleScrollThrottled"
            >
                <div
                    v-for="item in historyState.historyList"
                    :key="item.taskId"
                    class="history-menu-list__item pa-mb-4 pa-cursor-pointer pa-rounded-lg pa-p-2 hover:pa-bg-grey-20"
                    :class="{
                        'pa-bg-blue-40': item.taskId === taskId,
                    }"
                    @click="handleHistoryItem(item)"
                >
                    <div class="pa-flex pa-items-center pa-justify-between">
                        <div class="pa-text-xs pa-text-grey-90">
                            {{ HISTORY_TYPE_MAP[item.type] }}
                        </div>
                        <div>
                            <div
                                v-if="item.asyncStatus !== 'Complete'"
                                class="pa-flex pa-items-center"
                            >
                                <span
                                    class="th-loading th-loading-spinner th-loading-xs pa-text-primary"
                                />

                                <span
                                    class="pa-ml-1 pa-text-xs pa-text-eureka-600"
                                >
                                    {{ t('history.processing') }}
                                </span>
                            </div>

                            <div
                                v-else-if="!item.isRead"
                                class="pa-h-2 pa-w-2 pa-rounded-full pa-bg-red-500"
                            />
                        </div>
                    </div>
                    <Tooltip
                        :content="item.title || t('history.untitled')"
                        effect="light"
                        :visibleArrow="false"
                        placement="top"
                        popperClass="history-menu-title"
                        :openDelay="OPEN_DELAY"
                    >
                        <div
                            class="pa-line-clamp-1 pa-text-sm !pa-leading-6 pa-text-black"
                        >
                            {{ item.title || 'Untitled' }}
                        </div>
                    </Tooltip>
                    <div class="pa-text-xs pa-text-grey-90">
                        {{ item.updatedAt }}
                    </div>
                </div>
            </div>
        </div>

        <button
            v-else
            class="history-menu-btn th-btn th-btn-text pa-mt-4 pa-cursor-pointer !pa-rounded-l-none pa-bg-white hover:pa-bg-grey-20"
            @click.stop="handleHistoryMenu(true)"
        >
            <IconHistoryMenuOpen class="pa-m-1 pa-h-6 pa-w-6" />
        </button>
    </div>
</template>

<style lang="scss">
.history-menu {
    &-list {
        &-wrap {
            box-shadow: 0 0 4px 0 #0764e924, 0 4px 8px 0 #0764e914;
        }
    }

    &-btn {
        box-shadow: 0 0 4px 0 #0764e924, 0 4px 8px 0 #0764e914;
    }

    &-tooltip,
    &-title {
        border: 1px solid #dfe1e6 !important;
        margin-bottom: 4px !important;
        box-shadow: 0 2px 6px 0 #00000014;
    }
}
</style>
